import TemporaryOfferReview from "../components/TemporaryOfferReview";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInHub } from "../lib/hubContext";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import { fetchAngebote, type AngebotSort } from "../api/angebote";
import {
  FilterToolbar,
  TableHeadCell,
  TableLoadMoreFooter,
  TablePanel,
} from "../components/DataTable";
import EmptyState, { emptyStateActionClass } from "../components/EmptyState";
import DataRecovery from "../components/DataRecovery";
import SavedOfferSelection from "../components/SavedOfferSelection";
import { savedOfferDetailUrl, savedOfferIds } from "../lib/savedOfferNavigation";
import { isDemoMode } from "../lib/env";
import SearchInput from "../components/SearchInput";
import Select from "../components/Select";
import { formatDateShort, formatEuro, formatQty } from "../lib/format";
import { usePersonalView } from "../lib/usePersonalView";
import { useViewSearch } from "../lib/useViewSearch";
import PersonalViewNotice, { PersonalViewReset } from "../components/PersonalViewNotice";

const PAGE_SIZE = 50;

const SORT_OPTIONS: { value: AngebotSort; label: string }[] = [
  { value: "recent_desc", label: "Neueste zuerst" },
  { value: "amount_desc", label: "Höchste Summe zuerst" },
];

// Zwei Gesichter (#630): unter Bestellungen eine schlichte Angebotsliste,
// im Preisspiegel dieselbe Liste mit Auswahl für den Vergleich.
export default function AngeboteListPage({ mode = "list" }: { mode?: "list" | "picker" }) {
  const inHub = useInHub();
  const picker = mode === "picker";
  const personalView = usePersonalView("offers");
  const { params, setUrl: setParams } = personalView;
  const location = useLocation();
  const returnTo = location.pathname + location.search;
  const selected = savedOfferIds(params);
  function setSelected(next: string[]) {
    setParams(current => {
      const updated = new URLSearchParams(current);
      updated.delete("angebot");
      if (!updated.has("q") && !updated.has("sort")) updated.set("view", "default");
      [...new Set(next)].slice(0, 3).forEach(id => updated.append("angebot", id));
      return updated;
    }, { replace: true });
  }
  const { search, setSearch, searchPending } = useViewSearch(personalView);
  const sort: AngebotSort = params.get("sort") === "amount_desc" ? "amount_desc" : "recent_desc";
  function setSort(value: AngebotSort) {
    const next = new URLSearchParams(params);
    if (value === "recent_desc") next.delete("sort"); else next.set("sort", value);
    personalView.change(next);
  }
  const debouncedSearch = params.get("q")?.trim() ?? "";
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const angeboteQuery = useInfiniteQuery({
    queryKey: ["angebote", "list", { q: debouncedSearch, sort }],
    queryFn: ({ pageParam }) =>
      fetchAngebote({
        q: debouncedSearch || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    enabled: personalView.ready,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
    staleTime: 60_000,
  });

  const angebote = useMemo(
    () => angeboteQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [angeboteQuery.data],
  );
  const refreshing = !personalView.ready || searchPending || angeboteQuery.isPending || (angeboteQuery.isFetching && !angeboteQuery.isFetchingNextPage);
  const total = angeboteQuery.data?.pages[0]?.total ?? 0;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = angeboteQuery;
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!hasNextPage) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, loadMore]);

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      {picker ? (
        <header className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Preisspiegel</h1>
          <p className="mt-1 text-sm text-slate-600">Zwei oder drei Angebote auswählen und Position für Position nebeneinander vergleichen.</p>
        </header>
      ) : inHub ? (
        <div className="mb-3 flex justify-end">
          <Link to="/preisspiegel" className="text-sm font-medium text-emerald-700 hover:underline">Angebote im Preisspiegel vergleichen →</Link>
        </div>
      ) : null}

      {!isDemoMode() && <TemporaryOfferReview />}

      <section id="gespeicherte-angebote" tabIndex={-1} aria-label="Gespeicherte Angebote" className="scroll-mt-6 outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
      <PersonalViewNotice view={personalView} defaultLabel="Alle Angebote, neueste zuerst; Vergleichsauswahl bleibt erhalten" />
      <FilterToolbar>
        <SearchInput disabled={!personalView.ready}
          value={search}
          onChange={setSearch}
          placeholder="Belegnummer oder Großhändler..."
          ariaLabel="Angebot suchen"
          className="basis-full max-w-none sm:basis-auto"
        />
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          Sortierung
          <Select
            size="sm"
            value={sort}
            onChange={(event) => setSort(event.target.value as AngebotSort)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>
        <PersonalViewReset view={personalView} defaultLabel="Alle Angebote, neueste zuerst" className="ml-auto" />
      </FilterToolbar>

      {picker ? <SavedOfferSelection selected={selected} rows={angebote} onRemove={id => setSelected(selected.filter(value => value !== id))} onClear={() => setSelected([])} /> : null}
      <TablePanel
        title="Gespeicherte Angebote"
        subtitle={
          refreshing
            ? "Lade Angebote …"
            : angeboteQuery.isError ? undefined : `${total.toLocaleString("de-AT")} Angebote`
        }
        footer={
          refreshing || angeboteQuery.isError || !angebote.length ? null : (
            <TableLoadMoreFooter
              loadMoreRef={loadMoreRef}
              loadedCount={angebote.length}
              totalCount={total}
              itemLabel="Angeboten"
              moreItemLabel="Angebote"
              hasMore={Boolean(hasNextPage)}
              isLoading={isFetchingNextPage}
              buttonLabel="Weitere Angebote laden"
              loadingLabel="Lade weitere Angebote …"
              onLoadMore={loadMore}
            />
          )
        }
      >
        {refreshing ? <p role="status" className="p-6 text-sm text-slate-600">Lade Angebote …</p> : angeboteQuery.isError ? <div className="p-6"><DataRecovery error={angeboteQuery.error} subject="Angebote" onRetry={() => void angeboteQuery.refetch()} /></div> : !angebote.length ? <div className="p-6"><EmptyState title={debouncedSearch ? "Keine Angebote zu dieser Suche" : "Noch keine Angebote gespeichert"} actions={debouncedSearch ? <button className={emptyStateActionClass} onClick={() => setSearch("")}>Suche zurücksetzen</button> : !isDemoMode() ? <Link className={emptyStateActionClass} to="/imports">Angebote hochladen</Link> : undefined}>
          {debouncedSearch ? <>Für „{debouncedSearch}“ wurde kein gespeichertes Angebot gefunden. Versuche eine kürzere Belegnummer oder einen Großhändler. Deine Vergleichsauswahl bleibt erhalten.</> : isDemoMode() ? "In dieser Demo sind keine gespeicherten Angebote vorhanden." : "Angebote kommen über „Belege hochladen“ herein und erscheinen danach hier."}
        </EmptyState></div> : <table className="relative w-full min-w-[760px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {picker ? <TableHeadCell>Auswahl</TableHeadCell> : null}
              <TableHeadCell>Datum</TableHeadCell>
              <TableHeadCell>Belegnummer</TableHeadCell>
              <TableHeadCell>Großhändler</TableHeadCell>
              <TableHeadCell align="right">Positionen</TableHeadCell>
              <TableHeadCell align="right">Angebotssumme</TableHeadCell>
              <TableHeadCell align="right">
                <span className="sr-only">Aktion</span>
              </TableHeadCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {
              angebote.map((angebot) => (
                <tr key={angebot.angebot_id} className="hover:bg-slate-50">
                  {picker ? <td className="px-3 py-2"><input type="checkbox" aria-label={`Angebot ${angebot.doc_number ?? angebot.angebot_id} vergleichen`} checked={selected.includes(angebot.angebot_id)} disabled={!selected.includes(angebot.angebot_id) && selected.length >= 3} onChange={event => setSelected(event.target.checked ? [...selected, angebot.angebot_id] : selected.filter(id => id !== angebot.angebot_id))} /></td> : null}
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                    {formatDateShort(angebot.doc_date)}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={savedOfferDetailUrl(angebot.angebot_id, selected, returnTo)}
                      className="font-mono text-slate-900 hover:text-emerald-700 hover:underline"
                    >
                      {angebot.doc_number ?? angebot.angebot_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {angebot.supplier ?? "–"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {formatQty(angebot.line_count)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                    {formatEuro(angebot.positions_total)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={savedOfferDetailUrl(angebot.angebot_id, selected, returnTo)}
                      className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      Ansehen
                    </Link>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>}
      </TablePanel>
      </section>
    </main>
  );
}
